import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import DownloadIcon from "@mui/icons-material/Download";

import httpClient from "api/httpClient";

export default function ImportExcelDialog({
  open,
  title,
  endpoint,
  templateEndpoint,
  onClose,
  onDone,
  showSnackBar,
}) {
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const resetForm = () => {
    setFile(null);
    setUploading(false);
    setResult(null);
    setFileKey((k) => k + 1);
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const handleClose = () => {
    resetForm(); // optional but safe
    onClose?.();
  };

  const onPick = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
  };

  const onUpload = async () => {
    if (!file) {
      showSnackBar?.("Import", "error", "Please select an .xlsx file");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await httpClient.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setResult(res.data);
      showSnackBar?.("Import", "success", "Import finished");
      onDone?.(); // reload list
    } catch (err) {
      console.log(err);
      showSnackBar?.(
        "Import",
        "error",
        err?.response?.data?.message || err.message || "Import failed"
      );
    } finally {
      setUploading(false);
    }
  };

  // --- New Download Logic ---
  const onDownloadTemplate = async () => {
    try {
      const response = await httpClient.get(templateEndpoint, {
        responseType: "blob", // Important for binary data
      });

      // Create a blob link to click
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title.replace(/\s+/g, "_")}_Template.xlsx`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showSnackBar?.("Download", "error", "Could not download template");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          {title}
          {templateEndpoint && (
            <MDButton variant="text" color="info" onClick={onDownloadTemplate} size="small">
              <DownloadIcon fontSize="small" />
              &nbsp; Download Template
            </MDButton>
          )}
        </MDBox>
      </DialogTitle>

      <DialogContent>
        <MDBox mt={1}>
          <input key={fileKey} type="file" accept=".xlsx" onChange={onPick} />
        </MDBox>

        {result && (
          <MDBox mt={2}>
            <MDTypography variant="button">
              Total: {result.totalRows} | Imported: {result.imported} | Skipped: {result.skipped} |
              Failed: {result.failed} | Updated: {result.updated}
            </MDTypography>

            {Array.isArray(result.errors) && result.errors.length > 0 && (
              <MDBox mt={1}>
                <MDTypography variant="caption" color="error">
                  Errors:
                </MDTypography>
                {result.errors.slice(0, 20).map((e, i) => (
                  <MDTypography key={i} variant="caption" display="block" color="error">
                    Row {e.rowNumber}: {e.message}
                  </MDTypography>
                ))}
                {result.errors.length > 20 && (
                  <MDTypography variant="caption" display="block">
                    Showing first 20 errors...
                  </MDTypography>
                )}
              </MDBox>
            )}
          </MDBox>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={handleClose} disabled={uploading}>
          Close
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={onUpload} disabled={uploading}>
          {uploading ? "Uploading..." : "Import"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ImportExcelDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  templateEndpoint: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  showSnackBar: PropTypes.func,
};
